import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";

function StatCard({ label, value, unit, subtitle, color }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 0, md: 2 },
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#667085", fontWeight: 600 }}>
        {label}
      </Typography>
      {value ? (
        <Box
          sx={{ display: "flex", gap: "4px", alignItems: "baseline", color }}
        >
          <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{unit}</Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 24, fontWeight: 600, color: "#667085" }}>
          No Data
        </Typography>
      )}
      <Typography sx={{ color: "#667085", fontSize: 12, whiteSpace: "nowrap" }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unit: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  color: PropTypes.string,
};
StatCard.defaultProps = {
  value: null,
  color: "",
};

function getHRVStatus(hrv) {
  if (!hrv) return null;

  if (hrv > 50) {
    return {
      label: "Excellent (>50ms)",
      color: "#00A656",
      bgColor: "#00A6560D",
      borderColor: "#00A65626",
    };
  }
  if (hrv >= 25 && hrv <= 50) {
    return {
      label: "Good (25-50ms)",
      color: "#4280BE",
      bgColor: "#4280BE0D",
      borderColor: "#4280BE26",
    };
  }
  if (hrv >= 10 && hrv < 25) {
    return {
      label: "Fair (10-25ms)",
      color: "#FF9500",
      bgColor: "#FF95000D",
      borderColor: "#FF950026",
    };
  }
  if (hrv < 10) {
    return {
      label: "Low (<10ms)",
      color: "#D32F2F",
      bgColor: "#A600000D",
      borderColor: "#A6000326",
    };
  }
  return null;
}

export default function HRVCard({ lastMeasures, isLoading }) {
  const hrvStats = lastMeasures?.hrv_statistics;
  const lastSync = hrvStats?.last_sync
    ? new Date(hrvStats.last_sync).toLocaleString()
    : "-";

  const currentHRV = hrvStats?.current;
  const hrvStatus = getHRVStatus(currentHRV);

  const statistics = [
    {
      label: "Current",
      value: hrvStats?.current,
      unit: "ms",
      subtitle: `Last sync: ${lastSync}`,
    },
    {
      label: "Minimum",
      value: hrvStats?.minimum,
      unit: "ms",
      subtitle: "Lowest reading",
      color: "#FF1F56",
    },
    {
      label: "Maximum",
      value: hrvStats?.maximum,
      unit: "ms",
      subtitle: "Highest reading",
      color: "#4280BE",
    },
  ];

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        p: 2.5,
        borderRadius: 2,
        backgroundColor: "#FFF",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        opacity: isLoading ? 0.6 : 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            sx={{ fontWeight: 500, fontSize: 14, color: "#000000DE" }}
          >
            Heart Rate Variability
          </Typography>
          {hrvStatus && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: hrvStatus.bgColor,
                color: hrvStatus.color,
                border: `1px solid ${hrvStatus.borderColor}`,
                borderRadius: "8px",
                fontWeight: 500,
                fontSize: 12,
                p: "7px 8px",
              }}
            >
              <Box
                sx={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  backgroundColor: hrvStatus.color,
                }}
              />
              {hrvStatus.label}
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
          gap: 2.5,
          mb: 3,
        }}
      >
        <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
          <StatCard {...statistics[0]} />
        </Box>
        <StatCard {...statistics[1]} />
        <StatCard {...statistics[2]} />
      </Box>
    </Box>
  );
}

HRVCard.propTypes = {
  lastMeasures: PropTypes.shape({
    hrv_statistics: PropTypes.shape({
      current: PropTypes.number,
      minimum: PropTypes.number,
      maximum: PropTypes.number,
      last_sync: PropTypes.string,
    }),
  }),
  isLoading: PropTypes.bool,
};

HRVCard.defaultProps = {
  lastMeasures: {
    hrv_statistics: {
      current: null,
      minimum: null,
      maximum: null,
      last_sync: null,
    },
  },
  isLoading: false,
};
